import type { RowData, Table } from '@tanstack/react-table';

import type { FilterValue, SelectFilterValue } from '../types/filter-values';

export type UseDataTableFiltersReturn = {
  handleFilterChange: (columnId: string, value: FilterValue | undefined) => void;
  handleRemoveFilter: (columnId: string) => void;
  handleRemoveValue: (columnId: string, valueToRemove: string | number) => void;
  handleClearAll: () => void;
};

export function useDataTableFilters<TData extends RowData>(table: Table<TData>): UseDataTableFiltersReturn {
  function handleFilterChange(columnId: string, value: FilterValue | undefined) {
    const column = table.getColumn(columnId);
    if (column) {
      column.setFilterValue(value);
    }
  }

  function handleRemoveFilter(columnId: string) {
    const column = table.getColumn(columnId);
    if (column) {
      column.setFilterValue(undefined);
    }
  }

  function handleRemoveValue(columnId: string, valueToRemove: string | number) {
    const column = table.getColumn(columnId);
    if (!column) return;

    const currentValue = column.getFilterValue() as SelectFilterValue | undefined;
    if (!currentValue || !('values' in currentValue)) return;

    const newValues = currentValue.values.filter((v: unknown) => v !== valueToRemove);

    if (newValues.length === 0) {
      column.setFilterValue(undefined);
    } else {
      column.setFilterValue({
        ...currentValue,
        values: newValues,
      });
    }
  }

  function handleClearAll() {
    table.resetColumnFilters();
  }

  return {
    handleFilterChange,
    handleRemoveFilter,
    handleRemoveValue,
    handleClearAll,
  };
}
